import { PublishScheduleDialogProps } from '../types';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { PublishScheduleConfirmationConflicts } from './conflicts';
import { PublishScheduleConfirmationNotificationSummary } from './notificationSummary';
import { usePostHog } from '@posthog/react';
import { useIsPhone } from '@hooks/useIsPhone';
import { ActionSheet } from '@components/scheduling/actionSheet';

export const PublishScheduleConfirmationDialog = ({
  open,
  setOpen,
  onConfirm,
  disabled,
  date,
  conflicts,
  name,
  summaryDetail,
  customTitle,
  crewsWithoutPhoneIds,
  crewsWithPhoneIds = [],
  fieldUserAsCrewIds = [],
  uniqueForemenIds = [],
}: PublishScheduleDialogProps) => {
  const { t } = useTranslation('app');
  const posthog = usePostHog();
  const isPhone = useIsPhone();

  const totalCount = Array.from(
    new Set([...crewsWithPhoneIds, ...fieldUserAsCrewIds, ...uniqueForemenIds]),
  ).length;

  const title = customTitle
    ? t('publisher.customTitle', { name: name || '', count: totalCount || 0 })
    : t('publisher.date.title', { date, count: totalCount || 0 });

  const details = (
    <>
      <PublishScheduleConfirmationNotificationSummary
        crewsWithPhoneIds={crewsWithPhoneIds}
        crewsWithoutPhoneIds={crewsWithoutPhoneIds}
        fieldUserAsCrewIds={fieldUserAsCrewIds}
        uniqueForemenIds={uniqueForemenIds}
      />
      <PublishScheduleConfirmationConflicts
        conflicts={conflicts}
        name={name}
        summaryDetail={summaryDetail}
        date={date}
      />
    </>
  );

  const actions = (
    <AlertDialogFooter>
      <Button onClick={() => setOpen(false)} variant="secondary" disabled={disabled}>
        {t('actions.cancel')}
      </Button>
      <Button
        onClick={() => {
          posthog?.capture('schedule_published', {
            recipient_count: totalCount,
            has_conflicts: (conflicts?.length ?? 0) > 0,
          });
          onConfirm();
          setOpen(false);
        }}
        disabled={disabled}>
        {t('actions.sendSchedules')}
      </Button>
    </AlertDialogFooter>
  );

  if (isPhone)
    return (
      <ActionSheet
        open={open}
        onDismiss={() => setOpen(false)}
        title={title}
        description={t('publisher.description.first')}
        actions={actions}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-neutral-600">{t('publisher.description.second')}</p>
          {details}
        </div>
      </ActionSheet>
    );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{t('publisher.description.first')}</AlertDialogDescription>
          <AlertDialogDescription>{t('publisher.description.second')}</AlertDialogDescription>
        </AlertDialogHeader>
        {details}
        {actions}
      </AlertDialogContent>
    </AlertDialog>
  );
};
